import { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmtDate = (d) => (d ? new Date(d.endsWith('Z') ? d : `${d}Z`).toLocaleString('it-IT') : '-');

export const requestStatus = {
  in_attesa: { label: 'In attesa', cls: 'bg-amber-100 text-amber-700' },
  approvata: { label: 'Approvata', cls: 'bg-green-100 text-green-700' },
  rifiutata: { label: 'Rifiutata', cls: 'bg-red-100 text-red-700' },
};
const actionLabel = { identificazione: 'Identificazione', download: 'Download documento', invio_email: 'Invio documento via email' };

const RequestRow = ({ r, onDone }) => {
  const [busy, setBusy] = useState('');
  const decide = async (action) => {
    if (!window.confirm(action === 'approva' ? `Creare l'area clienti per ${r.name} e inviare l'invito?` : `Rifiutare la richiesta di ${r.name}?`)) return;
    setBusy(action);
    try {
      await axios.post(`${API}/admin/access-requests/${r.id}/${action}`, {}, authHeader());
      onDone();
    } catch (err) {
      alert(err.response?.data?.detail || 'Operazione non riuscita');
    } finally {
      setBusy('');
    }
  };
  const st = requestStatus[r.status] || requestStatus.in_attesa;
  return (
    <tr className="border-t" data-testid={`access-request-row-${r.id}`}>
      <td className="p-3"><p className="font-medium">{r.name}</p><p className="text-xs text-slate-500">{r.email}{r.phone ? ` · ${r.phone}` : ''}</p></td>
      <td className="p-3 font-mono text-xs">{r.fiscal_id}</td>
      <td className="p-3 text-xs">{r.numero_pratica || '-'}<br /><span className="text-slate-400">{fmtDate(r.created_at)}</span></td>
      <td className="p-3"><span className={`text-xs px-2 py-1 rounded-full ${st.cls}`} data-testid={`access-request-status-${r.id}`}>{st.label}</span></td>
      <td className="p-3">
        {r.status === 'in_attesa' && (
          <div className="flex gap-2">
            <Button size="sm" disabled={!!busy} onClick={() => decide('approva')} className="bg-green-600 hover:bg-green-700" data-testid={`access-request-approve-${r.id}`}>
              {busy === 'approva' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />} Approva
            </Button>
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => decide('rifiuta')} className="text-red-600 border-red-200" data-testid={`access-request-reject-${r.id}`}>
              <XCircle className="w-4 h-4 mr-1" /> Rifiuta
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
};

const AccessLog = ({ log }) => (
  <div className="bg-white rounded-xl shadow-sm overflow-x-auto" data-testid="access-log-table">
    <table className="w-full text-sm min-w-[640px]">
      <thead className="bg-slate-50 text-left text-slate-500"><tr><th className="p-3">Data</th><th className="p-3">Cliente</th><th className="p-3">Azione</th><th className="p-3">Dettaglio</th><th className="p-3">IP</th></tr></thead>
      <tbody>
        {log.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-400">Nessun accesso registrato</td></tr>}
        {log.map((l) => (
          <tr key={l.id} className="border-t">
            <td className="p-3 text-xs">{fmtDate(l.at)}</td>
            <td className="p-3"><p>{l.user_name}</p><p className="text-xs text-slate-500">{l.user_email}</p></td>
            <td className="p-3 text-xs">{actionLabel[l.action] || l.action}</td>
            <td className="p-3 text-xs">{l.detail || '-'}</td>
            <td className="p-3 text-xs font-mono">{l.ip}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const AdminAccessTab = ({ onCountChange, onDataChange }) => {
  const [requests, setRequests] = useState([]);
  const [log, setLog] = useState([]);
  const load = async () => {
    const [r, l] = await Promise.all([axios.get(`${API}/admin/access-requests`, authHeader()), axios.get(`${API}/admin/access-log`, authHeader())]);
    setRequests(r.data);
    setLog(l.data);
    onCountChange(r.data.filter((x) => x.status === 'in_attesa').length);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-8" data-testid="admin-access-tab">
      <div>
        <h2 className="text-2xl font-bold text-[#283880] mb-1">Richieste di accesso</h2>
        <p className="text-sm text-slate-500 mb-4">Richieste arrivate dall'assistente virtuale, con email già verificata dal richiedente.</p>
        <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-slate-50 text-left text-slate-500"><tr><th className="p-3">Richiedente</th><th className="p-3">CF / P.IVA</th><th className="p-3">Pratica / Data</th><th className="p-3">Stato</th><th className="p-3">Azioni</th></tr></thead>
            <tbody>
              {requests.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-400" data-testid="access-requests-empty">Nessuna richiesta</td></tr>}
              {requests.map((r) => <RequestRow key={r.id} r={r} onDone={() => { load(); onDataChange(); }} />)}
            </tbody>
          </table>
        </div>
      </div>
      <div>
        <h3 className="text-lg font-bold text-[#283880] mb-3">Registro accessi tramite assistente</h3>
        <AccessLog log={log} />
      </div>
    </div>
  );
};
